<!-- eslint-disable vuejs-accessibility/anchor-has-content -->
<template>
  <div
    class="product-card"
    :class="{
      [$class('loading')]: !product,
      'show-secondary-image': showSecondaryImage,
      'product-card--has-badge': productBadge,
    }"
    role="group"
    :aria-label="$string('product.card.product')"
  >
    <a
      class="product-card__image-container"
      :href="product.url"
      @blur="handleProductCardBlur"
      @focus="handleProductCardFocus"
      @mouseenter="handleProductCardFocus"
      @mouseleave="handleProductCardBlur"
    >
      <responsive-image
        class="product-card__primary-image"
        :image="product.featuredImage"
        :alt="getImgAlt(1)"
        min-max="200-500"
        ratio="231:269"
      />

      <responsive-image
        v-if="product.secondaryImage"
        class="product-card__secondary-image"
        :class="{
          'product-card__secondary-image--loaded': showSecondaryImage
        }"
        :image="product.secondaryImage"
        :alt="getImgAlt(2)"
        min-max="200-500"
        :padding="false"
      />

      <responsive-image
        v-if="loadActiveSwatch && activeSwatchImage"
        class="product-card__swatch-image"
        :class="{
          'product-card__swatch-image--loaded': showSwatchImage
        }"
        :image="activeSwatchImage"
        :alt="getImgAlt(3)"
        min-max="200-500"
        :padding="false"
      />
    </a>

    <div class="product-card__footer text-center">
      <h2
        class="
          product-card__title
          text-other-label-bold
        "
        v-text="productTitle"
      />

      <div
        v-if="!hideTagline"
        class="product-card__description text-body-m-regular"
        v-html="productTagline"
      />

      <product-prices
        class="product-card__prices"
        :compare-at-price="compareAtPrice"
        :price="price"
      />

      <color-swatches
        v-if="colorOptions"
        :color-options="colorOptions"
        :disabled-codes="disabledColorCodes"
        :featured-image-src="product.featuredImage?.src"
        is-product-card
        namespace="product-card"
        :product-handle="product.handle"
        :product-url="product.url"
        :render-plus-icon="renderPlusIcon"
        :variants="product.variants"
        @update-active-swatch="handleActiveSwatch"
      />

      <p
        v-if="productBadge"
        class="product-card__badge text-other-label-s"
        :style="{ color: productBadge.color }"
      >
        {{ productBadge.name }}
      </p>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product card (product-card)
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {Boolean} denimCardCopy - Whether to use title & tagline overrides.
 * @param {Boolean} [hideTagline] - Hide product description.
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Number} swatchLimit - Max # of swatches to render.
 *
 */
import ColorSwatches from '~async/color-swatches/color-swatches'
import ResponsiveImage from '~global/images/responsive-image'
import ProductPrices from '~global/product-prices/product-prices'

import { getDisabledColorCodes, isColorCodeInStr } from '~/helpers/product'

import { formatProduct } from '~/format/format-product'
import productsByTagQuery from '~graphql/productsByTag.gql'

export default {
  name: 'ProductCard',

  components: {
    ColorSwatches,
    ProductPrices,
    ResponsiveImage,
  },

  props: {
    denimCardCopy: {
      type: Boolean,
      default: false,
    },
    hideTagline: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    swatchLimit: {
      type: Number,
      default: 8,
    },
  },

  data() {
    return {
      activeSwatchImage: {},
      activeVariant: {},
      initialVariant: {},
      colorOptions: this.product.colorOptions[0],
      loadActiveSwatch: false,
      matchedProducts: [],
      renderPlusIcon: false,
      showSecondaryImage: false,
      showSwatchImage: false,
    }
  },

  computed: {

    /**
     * Return the product or active variant's compare at price.
     * @returns {Boolean|Number}
     */
    compareAtPrice() {
      return this.activeVariant
        ? this.activeVariant.compareAtPrice?.amount
        : this.product.compareAtPrice?.amount
    },

    /**
     * Get array of disabled color codes.
     * @returns {Array}
     */
    disabledColorCodes() {
      return getDisabledColorCodes(this.product.tags)
    },

    /**
     * Return the product or active variant's price.
     * @returns {Boolean|Number}
     */
    price() {
      return this.activeVariant
        ? this.activeVariant.price?.amount
        : this.product.price?.amount
    },

    /**
     * Compute product badge.
     * @returns {Boolean|String}
     */
    productBadge() {
      if (!this.product.tags) {
        return false
      }

      const badge = this.product.tags.find((tag) => tag.includes('badge:'))
      if (!badge) {
        return false
      }
      const parts = badge.replace('badge:', '').split(':')
      return {
        name: parts[0],
        color: parts[1] || '#000000',
      }
    },

    /**
     * Compute product title to render.
     * @returns {String}
     */
    productTitle() {
      if (!this.denimCardCopy || !this.product.denimCopyOverrides.title) {
        return this.product.title
      }

      return this.product.denimCopyOverrides.title
    },

    /**
     * Compute product tagline to render.
     */
    productTagline() {
      if (!this.denimCardCopy || !this.product.denimCopyOverrides.tagline) {
        return this.product.tagline
      }

      return this.product.denimCopyOverrides.tagline
    },

    /**
     * Style group tag.
     * @returns {String|Boolean}
     */
    styleGroupTag() {
      if (this.product?.giftCard) {
        return false
      }

      const styleGroupTag = this.product?.tags?.find(tag => tag.includes('style-group:')) ||
        this.liquid?.tags?.find(tag => tag.includes('style-group:'))

      return styleGroupTag ? styleGroupTag : false
    },
  },

  watch: {

    /**
     * Watch matched products to update swatch info.
     */
    matchedProducts() {
      this.setColorOptions()
    },

  },

  mounted() {
    this.setInitialVariant()
    this.setActiveVariant(this.initialVariant)
    this.queryMatchedProducts()
  },

  methods: {

    /**
     * Build new array based on existence of an index in a target array.
     * @param {Array} arrayOfValues - array that needs to be filtered.
     * @param {Array} arrayOfIndexes - array of indexes to include.
     * @returns {Array}
     */
    filteredArray(arrayOfValues, arrayOfIndexes) {
      return arrayOfValues.filter((_, index) => {
        return arrayOfIndexes.includes(index)
      })
    },

    /**
     * Handle events when a color swatch receives mouse or keyboard focus.
     * - Sets active swatch image and active variant.
     * @param {Object} colorData - Color data.
     */
    handleActiveSwatch(colorData) {
      const isSameProduct = colorData.handle === this.product.handle

      this.activeSwatchImage = isSameProduct
        ? this.product.hoverImages?.find(image => isColorCodeInStr(colorData.code, image?.src))
        : this.matchedProducts?.find(product => product.handle === colorData.handle)?.featuredImage

      if (this.activeSwatchImage) {
        this.loadActiveSwatch = true
      }

      if (this.loadActiveSwatch && !colorData?.initialSwatch) {
        this.showSwatchImage = true
      }

      const activeVariant = this.product.variants
        .find((variant) => isColorCodeInStr(colorData.code, variant.swatchUrl))

      this.setActiveVariant(activeVariant)
    },

    /**
     * Handle events when the product card image container loses focus.
     * - Reset any active swatch image,the secondary image states, and the
     *   active variant.
     */
    handleProductCardBlur() {
      this.activeSwatchImage = {}
      this.showSecondaryImage = false
      this.activeVariant = this.initialVariant
    },

    /**
     * Handle events when the product card image container gains focus.
     * - Remove active swatch image from colour swatch focus, and set the
     *   main secondary image state to true.
     */
    handleProductCardFocus() {
      this.activeSwatchImage = {}
      this.showSecondaryImage = true
    },

    /**
     * Get product card img alt.
     * @param {Number} number
     * @returns {String}
     */
    getImgAlt(number) {
      return this.$string('product.card.img_alt')
        .replace('{{ title }}', this.product.title)
        .replace('{{ number }}', number)
    },

    /**
     * Load products from GraphQL and sort currently active product to front.
     */
    async queryMatchedProducts() {
      if (!this.styleGroupTag) {
        this.setColorOptions()
        return
      }

      const query = `(tag:'${this.styleGroupTag}')`

      try {
        const response = await cnvs.Query({
          query: productsByTagQuery,
          variables: {
            country: this.$variable('store.country'),
            query,
            language: this.$variable('store.language'),
          },
        })

        if (!response.products.edges.length) {
          return
        }

        const tmpSwatches = []

        response.products.edges.forEach((product) => {
          const data = { product: product.node }

          tmpSwatches.push(formatProduct(data))
        })

        const activeProduct = tmpSwatches.find((product) => {
          return this.product.id === product.id
        })

        const index = tmpSwatches.indexOf(activeProduct)

        tmpSwatches.splice(index, 1)
        tmpSwatches.unshift(this.product)

        this.matchedProducts = tmpSwatches

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Set the active variant.
     * @param {Object} variant - variant object.
     */
    setActiveVariant(variant) {
      this.activeVariant = variant
    },

    /**
     * Set array of color options objects, one for each matched product.
     * @returns {Array|Boolean}
     */
    setColorOptions() {
      if (!this.product?.colorOptions || !this.product?.giftCard) {
        this.colorOptions = []
      }

      if (!this.matchedProducts?.length) {
        this.colorOptions = [
          {
            ...this.product.colorOptions[0],
            productIndex: 0,
            variants: this.product.variants,
          },
        ]

        return
      }

      const matchedColorOptions = []
      let renderedColorOptions = 0

      this.matchedProducts.forEach((matchedProduct, matchedProductIndex) => {
        if (renderedColorOptions >= this.swatchLimit) {
          this.renderPlusIcon = true
          return
        }

        if (!matchedProduct?.colorOptions.length) {
          return
        }

        matchedColorOptions.push({
          ...matchedProduct.colorOptions[0],
          productIndex: matchedProductIndex,
          variants: matchedProduct.variants,
        })

        renderedColorOptions += matchedProduct.colorOptions[0]?.colorCodes.length
      })

      this.colorOptions = matchedColorOptions.filter(options => options !== null)
    },

    /**
     * Set the initial variant.
     * - Save this data so that the appropriate information is displayed to the
     *   user when the product card loses focus.
     */
    setInitialVariant() {
      const activeCode = this.colorOptions?.colorCodes
        .find((code) => isColorCodeInStr(code, this.product.featuredImage?.src))

      this.initialVariant = this.product.variants
        .find((variant) => isColorCodeInStr(activeCode, variant.swatchUrl))
    },
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
